import produce from "immer";
import moment, { Moment } from "moment";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { number, object, string } from "yup";
import { LoyaltyLevel } from "../../../components/loyalty/utils";
import { Pageable } from "../../../services/pageable";
import { AllowedBlockchains } from "../../chain/models/internal";
import { getFields, validationFields } from "../../common/models/fields";
import { BillableApi, TransactionState } from "./internal";

interface Wallet {
  address: string;
  chain: AllowedBlockchains;
}

export namespace BillingDTO {
  export namespace Responses {
    export namespace GetSingleTransaction {
      export interface T {
        id: string;
        credentialsId: string;
        buyerEmailAddress: string;
        transactionHash: string;
        state: TransactionState;
        purchaseTime: Moment;
        credits: number;
        equivalentIn: { currency: "USDT" | "SKRT"; amount: number };
        buyerWallet: Wallet;
        sekuritanceWallet: Wallet | null;
      }

      export const parse = (body: any) =>
        produce<any, any, T>(body, (state: any) => {
          state.purchaseTime = state.purchaseTime ? moment.utc(state.purchaseTime) : state.purchaseTime;

          return state as T;
        });
    }
    export namespace GetAllTransactions {
      export interface T {
        content: GetSingleTransaction.T[];
        pageable: Pageable.Response.T;
      }

      export const defaults: T = {
        content: [],
        pageable: Pageable.Response.defaults,
      };

      export const parse = (body: any) =>
        produce<any, any, T>(body, (state: any) => {
          state.content = state.content.map(GetSingleTransaction.parse);
          return state as T;
        });
    }

    export namespace ConfirmTransaction {
      export interface T {}
    }

    export namespace GetSingleUsage {
      export interface T {
        id: string;
        apiCalledAt: Moment;
        service: string;
        featureNames: typeof BillableApi.T[];
        creditCost: number;
        creditCostIncVat: number;
      }

      export const parse = (body: any) =>
        produce<any, any, T>(body, (state: any) => {
          state.apiCalledAt = state.apiCalledAt ? moment.utc(state.apiCalledAt) : state.apiCalledAt;

          return state as T;
        });
    }
    export namespace GetAllUsages {
      export interface T {
        content: GetSingleUsage.T[];
        pageable: Pageable.Response.T;
      }

      export const defaults: T = {
        content: [],
        pageable: Pageable.Response.defaults,
      };

      export const parse = (body: any) =>
        produce<any, any, T>(body, (state: any) => {
          state.content = state.content.map(GetSingleUsage.parse);
          return state as T;
        });
    }

    export namespace GetPricing {
      export type T = {
        featureDetails: Array<{
          featureName: typeof BillableApi.T;
          originalPrice: number;
        }>;
        levelDiscounts: Array<{
          userLevel: typeof LoyaltyLevel.T | "NONE";
          discountRate: number;
        }>;
      };
    }

    export namespace GetSKRTAddresses {
      export type T = Array<{
        id: string;
        address: string;
        chain: AllowedBlockchains;
      }>;
    }

    export namespace Purchase {
      export interface T {
        id: string;
      }
    }

    export namespace ConfirmPurchase {
      export interface T {
        id: string;
      }
    }

    export namespace GetForexRate {
      export type T = { rate: number };
    }

    export namespace GetBalance {
      export interface T {
        credits: number;
        pendingCredits: number;
      }
    }

    export namespace GetBillingInfo {
      interface BillingServiceInfo {
        apiName: typeof BillableApi.T;
        remainingApiCalls: number;
      }

      export type T = {
        remainingPlatformCalls: number;
        apiSpecificInfo: Array<BillingServiceInfo>;
      };
    }
  }

  export namespace Requests {
    export namespace Purchase {
      export type T = {
        credits: number;
        buyerWallet: {
          address: string;
          chain: AllowedBlockchains;
        };
        currency: "USDT";
      };

      export const useFields = () => {
        const { t } = useTranslation("fields");
        const sourceWalletSchema = validationFields.sourceWallet.useSchema();
        const chainSchema = validationFields.chain.useSchema();

        const credits = {
          name: useMemo(() => t("credits"), [t]),
          schema: useMemo(() => number(), []),
        };
        const buyerWallet = {
          name: useMemo(() => t("source"), [t]),
          schema: useMemo(
            () =>
              object({
                address: sourceWalletSchema,
                chain: chainSchema,
              }),
            []
          ),
        };
        const currency = {
          name: useMemo(() => t("currency"), [t]),
          schema: useMemo(() => string(), []),
        };

        return useMemo(
          () =>
            getFields({
              credits,
              buyerWallet,
              currency,
            }),
          [credits.name, credits.schema, buyerWallet.name, buyerWallet.schema, currency.name, currency.schema]
        );
      };
    }

    export namespace ConfirmPurchase {
      export interface T {
        transactionHash: string;
        sekuritanceWallet: {
          address: string;
          chain: AllowedBlockchains;
        };
      }

      export const useFields = () => {
        const { t } = useTranslation("fields");
        const transactionHash = {
          name: useMemo(() => t("transaction-hash"), [t]),
          schema: useMemo(
            () =>
              string().required(
                t("required-field", {
                  ns: "errors",
                  field: t("transaction-hash"),
                })
              ),
            []
          ),
        };

        const destinationWalletSchema = validationFields.destinationWallet.useSchema();

        const sekuritanceWallet = {
          name: validationFields.destinationWallet.useName(),
          schema: destinationWalletSchema,
        };

        return useMemo(
          () =>
            getFields({
              transactionHash,
              sekuritanceWallet,
            }),
          [transactionHash.name, transactionHash.schema, sekuritanceWallet.name, sekuritanceWallet.schema]
        );
      };
    }
  }
}
